import { supabase } from '../supabaseClient';
import { resolveSiteTimeZone } from '../utils/siteScheduling';
import { buildIrishHolidayMap, getYearFromDateKey } from './payroll/holidays';
import { addDaysToDateKey, getWeekStartDateKey } from './payroll/time';
import { payrollService } from './payrollService';
import {
  earliestRateDate,
  statutoryOnlyContext,
  type ChargeRate,
  type CostingContext,
  type MinuteWindow,
  type ProfitabilityContract,
  type ProfitabilityInput,
  type ProfitabilityPeriod,
  type ProfitabilitySettings,
  type ProfitabilitySite,
  type RosteredShift,
} from './profitability';
import { siteService } from './siteService';

export * from './profitability';

/**
 * The Supabase side of contract profitability: charge rates, the two margin
 * assumptions, and the roster read. Everything that works a figure out is in
 * `services/profitability/`, which never imports this file.
 */

const DEFAULT_TIME_ZONE = 'Europe/Dublin';

/** Starting assumptions, used and labelled as such until a System Administrator saves their own. */
export const DEFAULT_PROFITABILITY_SETTINGS: ProfitabilitySettings = {
  employerOnCostPercent: 20,
  targetMarginPercent: 10,
  source: 'default',
};

/** Overtime weeks start on any weekday, so six days back always reaches the start of the first one. */
const WEEK_CONTEXT_DAYS = 6;

export interface SaveChargeRateInput {
  clientContractId: string;
  /** null sets the contract's own rate. */
  siteId: string | null;
  effectiveFrom: string;
  hourlyRate: number;
  sundayMultiplier: number;
  publicHolidayMultiplier: number;
  note: string | null;
}

/** Everything the Profitability page analyses, read once per period. */
export interface ProfitabilityWorkspace {
  input: ProfitabilityInput;
  sites: ProfitabilitySite[];
  currencyCode: string;
}

/** What the duty board needs to price the roster days inside a window. */
export interface RosterPay {
  context: CostingContext;
  currencyCode: string;
  /** Pay rates are only known for roster dates inside this window. */
  window: ProfitabilityPeriod;
}

interface SiteRecord {
  id: string;
  name: string | null;
  client_contract_id: string | null;
  country_code: string | null;
  timezone: string | null;
}

const toNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return value === null || value === undefined || !Number.isFinite(parsed) ? fallback : parsed;
};

function toWindows(value: unknown): MinuteWindow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((pair) => {
    if (!Array.isArray(pair)) return [];
    const startMinute = Number(pair[0]);
    const endMinute = Number(pair[1]);
    return Number.isFinite(startMinute) && Number.isFinite(endMinute) && endMinute > startMinute
      ? [{ startMinute, endMinute }]
      : [];
  });
}

export function toRosteredShifts(payload: unknown): RosteredShift[] {
  const record = (payload ?? {}) as { shifts?: unknown; guards?: unknown };
  const guards = (record.guards ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(record.shifts) ? record.shifts as Record<string, unknown>[] : [];

  return rows.map((row) => {
    const guardId = String(row.guard_id ?? '');
    return {
      id: String(row.id ?? ''),
      guardId,
      guardName: typeof guards[guardId] === 'string' ? guards[guardId] as string : 'Unknown guard',
      siteId: String(row.site_id ?? ''),
      dateKey: String(row.slot_date ?? ''),
      startMinute: toNumber(row.start_minute, 0),
      endMinute: toNumber(row.end_minute, 0),
      dutyWindows: toWindows(row.duty),
      unpaidBreakWindows: toWindows(row.unpaid_breaks),
      isPublished: row.is_published === true,
    };
  });
}

export function toChargeRate(row: Record<string, unknown>): ChargeRate {
  return {
    id: String(row.id ?? ''),
    clientContractId: String(row.client_contract_id ?? ''),
    siteId: typeof row.site_id === 'string' ? row.site_id : null,
    effectiveFrom: String(row.effective_from ?? ''),
    effectiveTo: typeof row.effective_to === 'string' ? row.effective_to : null,
    hourlyRate: toNumber(row.hourly_rate, 0),
    sundayMultiplier: toNumber(row.sunday_multiplier, 1),
    publicHolidayMultiplier: toNumber(row.public_holiday_multiplier, 1),
    note: typeof row.note === 'string' && row.note ? row.note : null,
  };
}

function toSite(row: SiteRecord, companyCountryCode: string | null): ProfitabilitySite {
  // sites.country_code is '' until somebody sets it; the agency's country then stands in.
  const countryCode = (row.country_code?.trim() || companyCountryCode?.trim() || '').toUpperCase();
  return {
    id: row.id,
    name: row.name?.trim() || 'Unknown site',
    clientContractId: row.client_contract_id,
    countryCode,
    timeZone: resolveSiteTimeZone({ timezone: row.timezone, country_code: countryCode || 'IE' })
      || DEFAULT_TIME_ZONE,
  };
}

const holidaysAround = (startDate: string, endDate: string): Map<string, string> => (
  buildIrishHolidayMap(getYearFromDateKey(startDate) - 1, getYearFromDateKey(endDate) + 1)
);

const earlier = (left: string, right: string): string => (left < right ? left : right);
const later = (left: string, right: string): string => (left > right ? left : right);

export const profitabilityService = {
  /** Every shift on the roster in a period, optionally for some guards only. Admins and managers. */
  async fetchRoster(startDate: string, endDate: string, guardIds?: string[]): Promise<RosteredShift[]> {
    const { data, error } = await supabase.rpc('profitability_roster', {
      p_start_date: startDate,
      p_end_date: endDate,
      p_guard_ids: guardIds ?? null,
    });
    if (error) throw error;
    return toRosteredShifts(data);
  },

  async fetchSites(): Promise<ProfitabilitySite[]> {
    const companyId = await siteService.getCurrentUserCompanyId();
    const [sites, company] = await Promise.all([
      supabase
        .from('sites')
        .select('id, name, client_contract_id, country_code, timezone')
        .eq('company_id', companyId)
        .order('name'),
      supabase.from('companies').select('country_code').eq('id', companyId).maybeSingle(),
    ]);

    if (sites.error) throw sites.error;
    if (company.error) throw company.error;

    const companyCountryCode = (company.data as { country_code?: string | null } | null)?.country_code ?? null;
    return ((sites.data ?? []) as SiteRecord[]).map((row) => toSite(row, companyCountryCode));
  },

  /** Archived clients included: a period in the past may still hold their shifts. */
  async fetchContracts(): Promise<ProfitabilityContract[]> {
    const companyId = await siteService.getCurrentUserCompanyId();
    const { data, error } = await supabase
      .from('client_contracts')
      .select('id, name, status')
      .eq('company_id', companyId)
      .order('name');

    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
      id: String(row.id ?? ''),
      name: String(row.name ?? ''),
      status: String(row.status ?? ''),
    }));
  },

  async fetchChargeRates(): Promise<ChargeRate[]> {
    const { data, error } = await supabase
      .from('client_charge_rates')
      .select('id, client_contract_id, site_id, effective_from, effective_to, hourly_rate, sunday_multiplier, public_holiday_multiplier, note')
      .order('effective_from', { ascending: false });

    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(toChargeRate);
  },

  /** Closes the rate it replaces the day before; the same date again corrects that rate. */
  async saveChargeRate(input: SaveChargeRateInput): Promise<void> {
    const { error } = await supabase.rpc('set_client_charge_rate', {
      p_client_contract_id: input.clientContractId,
      p_site_id: input.siteId,
      p_effective_from: input.effectiveFrom,
      p_hourly_rate: input.hourlyRate,
      p_sunday_multiplier: input.sundayMultiplier,
      p_public_holiday_multiplier: input.publicHolidayMultiplier,
      p_note: input.note?.trim() || null,
    });
    if (error) throw error;
  },

  /** The rate before it runs on in its place. */
  async deleteChargeRate(rateId: string): Promise<void> {
    const { error } = await supabase.rpc('delete_client_charge_rate', { p_rate_id: rateId });
    if (error) throw error;
  },

  async fetchSettings(): Promise<ProfitabilitySettings> {
    const { data, error } = await supabase
      .from('company_profitability_settings')
      .select('employer_on_cost_percent, target_margin_percent')
      .maybeSingle();

    if (error) throw error;
    if (!data) return DEFAULT_PROFITABILITY_SETTINGS;

    const row = data as Record<string, unknown>;
    return {
      employerOnCostPercent: toNumber(row.employer_on_cost_percent, DEFAULT_PROFITABILITY_SETTINGS.employerOnCostPercent),
      targetMarginPercent: toNumber(row.target_margin_percent, DEFAULT_PROFITABILITY_SETTINGS.targetMarginPercent),
      source: 'saved',
    };
  },

  async saveSettings(settings: Pick<ProfitabilitySettings, 'employerOnCostPercent' | 'targetMarginPercent'>): Promise<void> {
    const { error } = await supabase.rpc('save_profitability_settings', {
      p_employer_on_cost_percent: settings.employerOnCostPercent,
      p_target_margin_percent: settings.targetMarginPercent,
    });
    if (error) throw error;
  },

  /**
   * One period's roster with everything needed to price it. The page keeps the
   * result and re-runs the analysis as the scenario changes; nothing here
   * depends on the scenario, so that costs no round trip.
   */
  async loadWorkspace(period: ProfitabilityPeriod, today: string): Promise<ProfitabilityWorkspace> {
    const rosterStart = addDaysToDateKey(period.startDate, -WEEK_CONTEXT_DAYS);
    const [sites, contracts, chargeRates, settings, shifts] = await Promise.all([
      this.fetchSites(),
      this.fetchContracts(),
      this.fetchChargeRates(),
      this.fetchSettings(),
      this.fetchRoster(rosterStart, period.endDate),
    ]);

    // Rates are asked for on today's date and on the day before the last
    // statutory increase, as well as across the period itself.
    const countries = Array.from(new Set(sites.map((site) => site.countryCode).filter(Boolean)));
    const pay = await payrollService.fetchRosterPayContext(
      earlier(rosterStart, earliestRateDate(today, countries)),
      later(period.endDate, today),
    );

    return {
      input: {
        shifts,
        period,
        today,
        context: {
          settings: pay.settings,
          resolveRate: pay.resolveRate,
          sites: new Map(sites.map((site) => [site.id, site])),
          holidays: holidaysAround(rosterStart, later(period.endDate, today)),
          chargeRates,
        },
        contracts,
        settings,
      },
      sites,
      currencyCode: pay.settings.currencyCode,
    };
  },

  /**
   * What prices roster days on the duty board. Without the payroll permission
   * the database returns no rates, so `canSeePay` false asks for none and gives
   * back a context that only knows the statutory rules.
   */
  async loadRosterPay(window: ProfitabilityPeriod, canSeePay: boolean): Promise<RosterPay> {
    const sites = new Map((await this.fetchSites()).map((site) => [site.id, site]));
    const holidays = holidaysAround(window.startDate, window.endDate);

    if (!canSeePay) {
      return { context: statutoryOnlyContext(sites, holidays), currencyCode: 'EUR', window };
    }

    const pay = await payrollService.fetchRosterPayContext(window.startDate, window.endDate);
    return {
      context: {
        settings: pay.settings,
        resolveRate: pay.resolveRate,
        sites,
        holidays,
        chargeRates: [],
      },
      currencyCode: pay.settings.currencyCode,
      window,
    };
  },
};

/** The week a roster day's overtime is judged over; null while overtime is switched off. */
export function overtimeWeekFor(slotDate: string, pay: RosterPay): ProfitabilityPeriod | null {
  const { settings } = pay.context;
  if (!settings.overtimeEnabled) return null;

  const startDate = getWeekStartDateKey(slotDate, 'UTC', settings.overtimeWeekStartWeekday);
  return { startDate, endDate: addDaysToDateKey(startDate, WEEK_CONTEXT_DAYS) };
}
